import { useMutation } from '@apollo/client';
import React, { useCallback } from 'react';
import Modal, { ModalBody, ModalFooter } from '../../components/Modal';
import { Flex } from 'rebass';
import Button from '../../components/Button/Button';
import PropTypes from 'prop-types';
import useNotify from '../../hooks/useNotify';
import { gql } from '@/modes/company/gql';

type SubmitJobModalProps = {
  onClose: (actionTaken?: boolean) => void;
  assignmentIds: string[] | readonly string[];
};

const SUBMIT_JOBS = gql(`
  mutation SubmitInboundAssignments(
    $assignmentIds: [ID!]!
    $newStatus: AssignmentStatus!
  ) {
    setInboundAssignmentsStatus(
      assignmentIds: $assignmentIds
      newStatus: $newStatus
    ) {
      id
      status
      parent {
        id
        status
      }
      child {
        id
        status
      }
    }
  }
`);

const SubmitModal: React.FC<SubmitJobModalProps> = ({
  onClose,
  assignmentIds,
}) => {
  const { showError, showSuccess } = useNotify();

  const onCancel = useCallback(() => onClose(), []);
  const onActionComplete = useCallback(() => {
    showSuccess(
      `${assignmentIds.length} job${
        assignmentIds.length > 1 ? 's' : ''
      } submitted`
    );
    onClose(true);
  }, []);

  const [submitJobs, { loading: mutationLoading }] = useMutation(SUBMIT_JOBS, {
    onCompleted: onActionComplete,
    onError: useCallback((error) => {
      showError(
        `Error Submitting Job${assignmentIds.length > 1 ? 's' : ''}`,
        error
      );
    }, []),
  });

  return (
    <Modal
      title={`Submit ${
        assignmentIds.length > 1 ? assignmentIds.length : ''
      } Job${assignmentIds.length > 1 ? 's' : ''}`}
      onClose={onClose}
    >
      <ModalBody>
        <Flex p={3} flexDirection="column">
          Are you sure?
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="check-circle"
          disabled={mutationLoading}
          onClick={() => {
            submitJobs({
              variables: {
                assignmentIds: assignmentIds as string[],
                newStatus: 'SUBMITTED',
              },
            });
          }}
        >
          Yes
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onCancel}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

SubmitModal.propTypes = {
  onClose: PropTypes.any,
  assignmentIds: PropTypes.any.isRequired,
};

export default SubmitModal;
